import { useEffect } from 'react';
import { generatePath, useParams } from 'react-router-dom';
import { Form } from 'antd';
import { useJournalId } from 'hooks/useJournalId/useJournalId';
import { useSimpleNotification } from 'hooks/useSimpleNotification/useSimpleNotification';
import { useJournalDetailsBreadcrumb } from 'pages/app/staff/journal/journalDetails/hooks';
import { SpecialConsultationFormFields } from 'types';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';
import { appPaths } from 'consts/paths/paths';
import { useAppMutation } from 'services/reactQuery/useAppMutation';
import { ErrorDisplay } from 'components/molecules';
import { formFields } from 'consts/form/formFields';
import { journalTexts } from 'consts/text';
import { parseSpecialConsultationFormFieldsIntoAddUpdateSpecialConsultationDTO } from 'utils/parsers/specialConsultation/parseSpecialConsultationFormFieldsIntoAddUpdateGroupConsultationDTO';
import { SpecialConsultationId } from 'types/specialConstultation/specialConsultationDTO';
import { parseSpecialConsultationDTOIntoSpecialConsultationFormFields } from 'utils/parsers/specialConsultation/parseSpecialConsultationDTOIntoSpecialConsultationFormFields';
import { SpecialJournalConsultationAddEditForm } from 'components/templates';

import { useNavigateWithState } from 'hooks/useNavigateWithState/useNavigateWithState';

export const SpecialJournalConsultationEditPage = () => {
    const { journalId } = useJournalId();
    const { displaySuccess } = useSimpleNotification();
    const [form] = Form.useForm();
    const { statefulNavigate } = useNavigateWithState();
    const { consultationId: consultationIdParam } = useParams();

    useJournalDetailsBreadcrumb({
        journalId,
        journalType: 'SPECIAL',
    });

    const consultationId = (consultationIdParam || '') as SpecialConsultationId;

    const {
        data: consultationData,
        isFetching: isConsultationDataFetching,
        isError: isFetchConsultationDataError,
    } = useAppQuery(
        'SPECIAL_CONSULTATION',
        [journalId, consultationId],
        () =>
            StaffHTTPService.specialConsultations.getSpecialConsultation(journalId, consultationId),
        { refetchOnWindowFocus: false },
    );

    useEffect(() => {
        if (consultationData && !form.getFieldValue(formFields.subject)) {
            form.setFieldsValue(
                parseSpecialConsultationDTOIntoSpecialConsultationFormFields(consultationData),
            );
        }
    }, [consultationData, form]);

    const navigateToConsultationsPage = () =>
        statefulNavigate(
            generatePath(
                appPaths.app.journals.specialJournalDetails.pickedJournal.consultations.base,
                { journalId },
            ),
            { replace: true },
        );

    const handleUpdateConsultationSuccess = () => {
        displaySuccess(
            journalTexts.journalDetails.common.consultations.successfullyUpdatedConsultation,
        );
        navigateToConsultationsPage();
    };

    const {
        mutate: updateConsultation,
        error: updateConsultationError,
        isLoading: isUpdateConsultationLoading,
    } = useAppMutation(StaffHTTPService.specialConsultations.updateSpecialConsultation, {
        key: ['UPDATE_SPECIAL_CONSULTATION'],
        onSuccess: handleUpdateConsultationSuccess,
        invalidateQueryKeys: [
            ['SPECIAL_CONSULTATIONS', journalId],
            ['SPECIAL_CONSULTATION', journalId, consultationId],
        ],
    });

    const handleUpdateConsultationFinish = (
        consultationFormData: SpecialConsultationFormFields,
    ) => {
        const parsedUpdateConsultationDTO =
            parseSpecialConsultationFormFieldsIntoAddUpdateSpecialConsultationDTO(
                consultationFormData,
            );
        updateConsultation({
            journalId,
            consultationId,
            updateSpecialConsultationDTO: parsedUpdateConsultationDTO,
        });
    };

    if (isFetchConsultationDataError) {
        return <ErrorDisplay />;
    }

    return (
        <SpecialJournalConsultationAddEditForm
            mode="edit"
            journalId={journalId}
            form={form}
            axiosError={updateConsultationError}
            isLoading={isConsultationDataFetching || isUpdateConsultationLoading}
            onFinish={handleUpdateConsultationFinish}
        />
    );
};
